import React, { createContext, useState, useContext, useCallback, useEffect, useMemo } from 'react';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        setUser(await response.json());
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Refresh access when the page becomes visible again. Window focus also
  // changes around the Android keyboard in fullscreen; refreshing on every
  // focus needlessly fetches /auth/me and rerenders the entire app.
  useEffect(() => {
    const refreshAccess = () => { if (document.visibilityState === 'visible') checkAuth(); };
    document.addEventListener('visibilitychange', refreshAccess);
    return () => {
      document.removeEventListener('visibilitychange', refreshAccess);
    };
  }, [checkAuth]);

  const logout = async () => {
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });
    setUser(null);
  };

  const access = user?.access || {};
  const features = useMemo(() => Array.isArray(access.features) ? access.features : [], [access.features]);
  const hasFeature = useCallback((feature) => !feature || features.includes(feature), [features]);

  const value = useMemo(() => ({
    user,
    setUser,
    loading,
    checkAuth,
    logout,
    access,
    plan: access.plan || null,
    addons: access.addons || {},
    features,
    licenseStatus: access.license_status || null,
    hasFeature,
    role: user?.role || null,
    isAdmin: user?.role === 'admin'
  }), [user, loading, checkAuth, access, features, hasFeature]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
